import { Link } from "react-router-dom";
import { useApprovedBriefs } from "../../hooks/useApprovals";
import {
  approvalPublishNote,
  approvedBriefPath,
  approvedStateLabel,
  approverLabel,
  formatApprovalTime,
} from "../../lib/approval-display";
import { surfaceLabel } from "../../lib/surface-paths";

/** Review → Approved: approved drafts leave the queue but stay findable here, each linked to its brief. */
export function ApprovedDraftsList({ brandId }: { brandId: string }) {
  const approved = useApprovedBriefs(brandId);
  if (approved.isLoading) return <p className="text-sm text-slate-500">Loading approved drafts…</p>;
  if (approved.isError) {
    return <p role="alert" className="text-sm text-red-800">{approved.error instanceof Error ? approved.error.message : "Could not load approved drafts."}</p>;
  }
  const items = approved.data ?? [];
  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">No approved drafts yet. Drafts you approve stay listed here.</p>
      ) : (
        <ul aria-label="Approved drafts" className="divide-y divide-slate-200 rounded-xl border border-slate-200">
          {items.map((item) => {
            const when = formatApprovalTime(item.approved_at);
            return (
              <li key={item.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{item.title}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {[item.surface ? surfaceLabel(item.surface) : null, item.campaign_name].filter(Boolean).join(" · ")}
                  </p>
                  <p className="mt-1 text-sm text-slate-700">
                    {when ? `Approved by ${approverLabel(item.approved_by)} on ${when}` : "Approved (time not recorded)"}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">{approvalPublishNote(item.surface)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
                    {approvedStateLabel(item.state)}
                  </span>
                  <Link to={approvedBriefPath(brandId, item.campaign_id, item.id)} className="inline-flex min-h-11 items-center text-sm font-medium text-brand-700 hover:underline">
                    Open brief
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
